/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useState } from 'react';
import { makeStyles, Tab } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import WelcomeSection from 'components/welcomeSection/WelcomeSection';
import Button from 'components/common/Button';
import InviteUserDialog from 'components/user/inviteUserDialog';
import { useInviteUserDialog } from 'components/user/useInviteUserDialog';
import InviteUsersSuccessDialog from 'components/user/inviteUsersSuccessDialog';
import useOrganization from 'organization/useOrganization';
import { RoutePath } from 'shared/enums/RoutePath';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { TabContext, TabList } from '@material-ui/lab';
import { IsInRole } from 'shared/authorization/IsInRole';
import { Roles } from 'shared/enums/Roles';
import { selectAuthUserData } from 'redux/features/auth/auth.slice';
import { useUserAccounts } from 'redux/features/user/userAccounts/useUserAccounts';
import { selectVersion } from '../../redux/features/configuration/configuration.slice';
import { dataTestId } from '../../shared/utils/versionUtils';
import InvitationsTable from './components/InvitationsTable';
import UserTable from './components/UserTable';
import useRemoveUsersFromOrg from '../../redux/features/users/remove/useRemoveUsersFromOrg';
import RemoveUsersDialog from '../../components/user/removeUsersDialog';
import { showTab, tablesType } from './components/userManagementPage.helper';
import { isRoleValid } from '../../redux/features/auth/auth.helper';

const useStyles = makeStyles(theme => ({
  userActionsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    position: 'relative',
    height: 'auto',
  },
  buttons: {
    display: 'flex',
    flexFlow: 'row wrap',
    justifyContent: 'space-between',
    marginBottom: '1.5rem',
    marginTop: '1.125rem',
    marginLeft: 'auto',
  },
  actionButton: {
    marginRight: '1rem',
  },
  buttonsBottom: {
    display: 'flex',
    flexFlow: 'row wrap',
    marginTop: '1.5rem',
    marginBottom: '1.5rem',
    marginRight: 'auto',
  },
  tabList: {
    borderBottomStyle: 'solid',
    borderBottomColor: theme.palette.tabSectionUnderline,
    borderBottomWidth: '1px',
  },
}));

export default function UserManagementPage() {
  const classes = useStyles();
  const [displayedTable, setDisplayedTable] = useState(tablesType.userTable.name);
  const { user } = useUserAccounts();

  const { t } = useTranslation();
  const history = useHistory();
  const selectedVersion = useSelector(selectVersion);

  const currentOrganization = useOrganization();
  const userData = useSelector(selectAuthUserData);

  const initials = user?.initials ?? `${user?.firstName.charAt(0)}${user?.lastName.charAt(0)}`.toUpperCase();

  const { removeDialog, deleteButton, onToggleRemoveDialog, onRemoveUsersConfirm } = useRemoveUsersFromOrg(
    currentOrganization?.id,
  );

  const {
    isLoading,
    dialog,
    isValid,
    autofillUsers,
    successDialog,
    onToggleSuccessDialog,
    onSubmit,
    onToggleDialog,
    onRoleChange,
    onUserChange,
    onKeyUp,
    validateSelection,
  } = useInviteUserDialog(currentOrganization?.id);

  const subtitle = currentOrganization?.type === 'INDIVIDUAL' ? t('INDIVIDUAL') : currentOrganization?.name;

  return (
    <div>
      <WelcomeSection
        showUserProfile={true}
        title={t('user-management.my.organisation')}
        subtitle={subtitle}
        description={currentOrganization?.attributes?.description}
        initials={initials}
        name={userData?.name}
      />

      <TabContext value={displayedTable}>
        <div className={classes.tabList}>
          <TabList
            onChange={(_, value) => setDisplayedTable(value)}
            textColor="primary"
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            {Object.values(tablesType).map((table) => {
              if (isRoleValid(userData?.roles, table.roles) && showTab(table, currentOrganization))
                return <Tab key={table.name} fullWidth label={t(table.name)} value={table.name} />;

              return <></>;
            })}
          </TabList>
        </div>
      </TabContext>

      <section
        aria-labelledby="user-management-actions"
        aria-describedby="user-management-actions"
        className={classes.userActionsContainer}
      >
        <div className={classes.buttons}>
          <IsInRole roles={[Roles.END_USER_ADMIN]}>
            <Button
              disabled={currentOrganization?.type === 'INDIVIDUAL'}
              className={classes.actionButton}
              color="primary"
              {...dataTestId({
                value: `inviteButton-${selectedVersion.value}`,
              })}
              onClick={() => onToggleDialog(true)}
            >
              {t('user-management.invite')}
            </Button>

            {deleteButton.visible && (
              <Button
                color="secondary"
                {...dataTestId({
                  value: `deleteButton-${selectedVersion.value}`,
                })}
                onClick={() => onToggleRemoveDialog(true)}
              >
                {t('user-management.remove.user')}
              </Button>
            )}
          </IsInRole>
        </div>
      </section>

      {displayedTable === tablesType.invitationTable.name && (
        <InvitationsTable currentOrganization={currentOrganization} />
      )}
      {displayedTable === tablesType.userTable.name && <UserTable currentOrganization={currentOrganization} />}

      <div className={classes.buttonsBottom}>
        <IsInRole roles={[Roles.END_USER_ADMIN]}>
          <Button
            disabled={currentOrganization?.type === 'INDIVIDUAL'}
            color="primary"
            id="editButton"
            onClick={() => {
              history.push(
                RoutePath.generateRoutePath(RoutePath.ORGANIZATION_EDIT, {
                  organisationId: currentOrganization.id,
                }),
              );
            }}
          >
            {t('user-management.edit.organisation')}
          </Button>
        </IsInRole>
      </div>

      {dialog.open && (
        <InviteUserDialog
          isLoading={isLoading}
          dialog={dialog}
          isValid={isValid}
          autofillUsers={autofillUsers}
          validateSelection={validateSelection}
          onSubmit={onSubmit}
          onToggleDialog={onToggleDialog}
          onRoleChange={onRoleChange}
          onUserChange={onUserChange}
          onKeyUp={onKeyUp}
        />
      )}

      {successDialog.open && (
        <InviteUsersSuccessDialog successDialog={successDialog} onToggleSuccessDialog={onToggleSuccessDialog} />
      )}

      {removeDialog.open && (
        <RemoveUsersDialog dialog={removeDialog} toggleDialog={onToggleRemoveDialog} onSubmit={onRemoveUsersConfirm} />
      )}
    </div>
  );
}
